import type { ApiClient } from '../../core/api-client.js';
import { ApiError } from '../../core/api-client.js';
import type { Resultado } from '../../core/resultado.js';

/**
 * Adapter do módulo Integrações no frontend — único ponto que conhece o
 * formato de `/api/v1/integracoes/melhor-envio/*`
 * (`apps/api/src/modules/integracoes/integracoes.routes.ts`). Essas rotas
 * são um proxy fino para o site legado (`/api/erp-bridge/melhor-envio/*`):
 * o ERP não tem estado próprio dessa integração.
 */
export interface StatusMelhorEnvio {
  connected: boolean;
  status: 'connected' | 'expired' | 'disconnected';
  accountName: string | null;
  connectedAt: string | null;
  tokenExpiresAt: string | null;
  canAutoRefresh: boolean;
  scopes: string[];
  clientId: string | null;
  redirectUri: string | null;
  hasStaticTokenFallback: boolean;
  checkedAt: string;
}

export interface ResultadoTesteMelhorEnvio {
  ok: boolean;
  accountName: string | null;
  error?: string;
}

interface StatusRespostaApi {
  connected: boolean;
  status: 'connected' | 'expired' | 'disconnected';
  account_name: string | null;
  connected_at: string | null;
  token_expires_at: string | null;
  can_auto_refresh: boolean;
  scopes: string[];
  client_id: string | null;
  redirect_uri: string | null;
  has_static_token_fallback: boolean;
  checked_at: string;
}

function paraStatus(r: StatusRespostaApi): StatusMelhorEnvio {
  return {
    connected: r.connected,
    status: r.status,
    accountName: r.account_name,
    connectedAt: r.connected_at,
    tokenExpiresAt: r.token_expires_at,
    canAutoRefresh: r.can_auto_refresh,
    scopes: r.scopes,
    clientId: r.client_id,
    redirectUri: r.redirect_uri,
    hasStaticTokenFallback: r.has_static_token_fallback,
    checkedAt: r.checked_at,
  };
}

export class IntegracoesApi {
  constructor(private readonly api: ApiClient) {}

  async statusMelhorEnvio(): Promise<Resultado<StatusMelhorEnvio>> {
    try {
      return { status: 'ok', dado: paraStatus(await this.api.get<StatusRespostaApi>('/integracoes/melhor-envio/status')) };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      if (erro instanceof ApiError && erro.status === 503) {
        return { status: 'indisponivel', motivo: erro.message };
      }
      return {
        status: 'erro',
        mensagem: erro instanceof ApiError ? erro.message : 'falha ao consultar o Melhor Envio',
      };
    }
  }

  async testarMelhorEnvio(): Promise<ResultadoTesteMelhorEnvio> {
    const r = await this.api.post<{ ok: boolean; account_name: string | null; error?: string }>(
      '/integracoes/melhor-envio/testar'
    );
    return { ok: r.ok, accountName: r.account_name, ...(r.error !== undefined ? { error: r.error } : {}) };
  }

  async desconectarMelhorEnvio(): Promise<void> {
    await this.api.post('/integracoes/melhor-envio/desconectar');
  }

  /** Devolve a URL de autorização do Melhor Envio — a tela redireciona o navegador para ela. */
  async urlAutorizacaoMelhorEnvio(returnTo: string): Promise<string> {
    const r = await this.api.get<{ url: string }>('/integracoes/melhor-envio/url-autorizacao', {
      return_to: returnTo,
    });
    return r.url;
  }
}
